import React from 'react';
import { Radio as AntRadio } from 'antd';
import type { RadioProps as AntRadioProps, RadioGroupProps as AntRadioGroupProps } from 'antd';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';

// ============================================================================
// Types
// ============================================================================

export type RadioSize = 'small' | 'middle' | 'large';
export type RadioButtonStyle = 'outline' | 'solid';
export type RadioDirection = 'horizontal' | 'vertical';

export interface RadioProps extends Omit<AntRadioProps, 'size'> {
    /** Size of the radio button */
    size?: RadioSize;
    /** Custom label for the radio */
    label?: React.ReactNode;
    /** Description content below the label */
    description?: React.ReactNode;
    /** Additional className */
    className?: string;
}

export interface RadioGroupProps extends Omit<AntRadioGroupProps, 'size' | 'optionType'> {
    /** Size of radio buttons in the group */
    size?: RadioSize;
    /** Direction of the radio group */
    direction?: RadioDirection;
    /** Gap between radio items */
    gap?: 'small' | 'medium' | 'large';
    /** Additional className */
    className?: string;
}

export interface RadioButtonGroupProps extends Omit<AntRadioGroupProps, 'size' | 'optionType'> {
    /** Size of radio buttons */
    size?: RadioSize;
    /** Button style variant */
    buttonStyle?: RadioButtonStyle;
    /** Additional className */
    className?: string;
}

export interface RadioOption {
    label: React.ReactNode;
    value: string | number | boolean;
    disabled?: boolean;
    description?: React.ReactNode;
}

export interface RadioCardProps extends Omit<AntRadioProps, 'size' | 'title'> {
    /** Card title */
    title: React.ReactNode;
    /** Card description */
    description?: React.ReactNode;
    /** Icon to display */
    icon?: React.ReactNode;
    /** Size variant */
    size?: RadioSize;
    /** Additional className */
    className?: string;
}

// ============================================================================
// Size Configurations
// ============================================================================

const sizeConfig = {
    small: {
        radio: 14,
        innerDot: 6,
        fontSize: 'text-xs',
        padding: 'py-1',
        gap: 'gap-1.5',
    },
    middle: {
        radio: 16,
        innerDot: 8,
        fontSize: 'text-sm',
        padding: 'py-1.5',
        gap: 'gap-2',
    },
    large: {
        radio: 20,
        innerDot: 10,
        fontSize: 'text-base',
        padding: 'py-2',
        gap: 'gap-2.5',
    },
};

const gapConfig = {
    small: 'gap-2',
    medium: 'gap-4',
    large: 'gap-6',
};

// ============================================================================
// CSS Styles
// ============================================================================

const getRadioStyles = (size: RadioSize) => {
    const config = sizeConfig[size];
    return `
    .ds-radio-${size} .ant-radio {
      top: 0;
    }
    .ds-radio-${size} .ant-radio-inner {
      width: ${config.radio}px;
      height: ${config.radio}px;
      border-color: ${colors.surfaceDark};
      background-color: ${colors.white};
      transition: all 0.2s ease-in-out;
    }
    .ds-radio-${size} .ant-radio-inner::after {
      width: ${config.innerDot}px;
      height: ${config.innerDot}px;
      inset-block-start: 50%;
      inset-inline-start: 50%;
      margin-block-start: ${-config.innerDot / 2}px;
      margin-inline-start: ${-config.innerDot / 2}px;
      background-color: ${colors.primary};
      border-radius: 50%;
    }
    .ds-radio-${size} .ant-radio-checked .ant-radio-inner {
      border-color: ${colors.primary};
      border-width: 2px;
      background-color: ${colors.white};
    }
    .ds-radio-${size} .ant-radio-checked .ant-radio-inner::after {
      transform: scale(1) !important;
      opacity: 1 !important;
    }
    .ds-radio-${size} .ant-radio-wrapper-checked {
      font-weight: 600;
    }
    .ds-radio-${size} .ant-radio:hover .ant-radio-inner {
      border-color: ${colors.primary};
    }
    .ds-radio-${size} .ant-radio-disabled .ant-radio-inner {
      background-color: ${colors.surfaceLighter};
      border-color: ${colors.surfaceSubtle};
    }
    .ds-radio-${size} .ant-radio-disabled .ant-radio-inner::after {
      background-color: ${colors.surfaceDark};
    }
    .ds-radio-${size} .ant-radio-wrapper {
      color: ${colors.textDefault};
      align-items: center;
    }
    .ds-radio-group-vertical.ds-radio-${size} .ant-radio-wrapper {
      width: 100%;
      margin-inline-end: 0;
    }
    .ds-radio-${size} .ant-radio-wrapper-disabled {
      color: ${colors.textMuted};
    }
  `;
};

const getRadioButtonStyles = (buttonStyle: RadioButtonStyle) => `
  .ds-radio-button-group .ant-radio-button-wrapper {
    border-color: ${colors.surfaceSubtle};
    color: ${colors.textDefault};
    background-color: ${colors.white};
    transition: all 0.2s ease-in-out;
  }
  .ds-radio-button-group .ant-radio-button-wrapper:hover {
    color: ${colors.primary};
    border-color: ${colors.primary};
  }
  .ds-radio-button-group .ant-radio-button-wrapper-checked {
    border-color: ${colors.primary};
    color: ${buttonStyle === 'solid' ? colors.white : colors.primary};
    background-color: ${buttonStyle === 'solid' ? colors.primary : colors.white};
  }
  .ds-radio-button-group .ant-radio-button-wrapper-checked:hover {
    border-color: ${colors.primaryHover};
    color: ${buttonStyle === 'solid' ? colors.white : colors.primaryHover};
    background-color: ${buttonStyle === 'solid' ? colors.primaryHover : colors.white};
  }
  .ds-radio-button-group .ant-radio-button-wrapper-checked::before {
    background-color: ${colors.primary};
  }
  .ds-radio-button-group .ant-radio-button-wrapper-disabled {
    color: ${colors.textMuted};
    background-color: ${colors.surfaceLighter};
    border-color: ${colors.surfaceSubtle};
  }
  .ds-radio-button-group .ant-radio-button-wrapper-disabled.ant-radio-button-wrapper-checked {
    color: ${colors.textMuted};
    background-color: ${colors.surfaceSubtle};
  }
  .ds-radio-button-group.ant-radio-group-small .ant-radio-button-wrapper {
    height: 28px;
    line-height: 26px;
    padding-inline: 12px;
    font-size: 12px;
  }
  .ds-radio-button-group.ant-radio-group-middle .ant-radio-button-wrapper {
    height: 36px;
    line-height: 34px;
    padding-inline: 16px;
    font-size: 14px;
  }
  .ds-radio-button-group.ant-radio-group-large .ant-radio-button-wrapper {
    height: 44px;
    line-height: 42px;
    padding-inline: 20px;
    font-size: 16px;
  }
`;

// ============================================================================
// Radio Component
// ============================================================================

export const Radio: React.FC<RadioProps> = ({
    size = 'middle',
    label,
    description,
    className,
    children,
    ...props
}) => {
    const config = sizeConfig[size];

    const content = label || children ? (
        <span className={cn('flex flex-col', config.gap)}>
            <span className={cn(config.fontSize, 'font-medium')} style={{ color: colors.textDefault }}>
                {label || children}
            </span>
            {description && (
                <span className="text-xs" style={{ color: colors.textMuted }}>
                    {description}
                </span>
            )}
        </span>
    ) : undefined;

    return (
        <>
            <style>{getRadioStyles(size)}</style>
            <AntRadio
                className={cn(`ds-radio-${size}`, className)}
                {...props}
            >
                {content}
            </AntRadio>
        </>
    );
};

// ============================================================================
// Radio Group Component
// ============================================================================

export const RadioGroup: React.FC<RadioGroupProps> = ({
    size = 'middle',
    direction = 'horizontal',
    gap = 'medium',
    className,
    children,
    options,
    ...props
}) => {
    const isVertical = direction === 'vertical';
    const directionClass = isVertical ? 'flex-col' : 'flex-row flex-wrap';

    // If options are provided, render them
    const renderOptions = () => {
        if (!options) return children;

        return options.map((option) => {
            if (typeof option === 'string' || typeof option === 'number') {
                return (
                    <Radio key={String(option)} value={option} size={size}>
                        {option}
                    </Radio>
                );
            }

            const opt = option as RadioOption;
            return (
                <Radio
                    key={String(opt.value)}
                    value={opt.value}
                    disabled={opt.disabled}
                    size={size}
                    label={opt.label}
                    description={opt.description}
                />
            );
        });
    };

    return (
        <>
            <style>{getRadioStyles(size)}</style>
            <AntRadio.Group
                className={cn(
                    `ds-radio-${size}`,
                    isVertical && 'ds-radio-group-vertical',
                    'flex',
                    directionClass,
                    gapConfig[gap],
                    className
                )}
                style={{
                    display: 'flex',
                    flexDirection: isVertical ? 'column' : 'row',
                    flexWrap: isVertical ? 'nowrap' : 'wrap',
                    alignItems: isVertical ? 'flex-start' : 'center',
                }}
                {...props}
            >
                {renderOptions()}
            </AntRadio.Group>
        </>
    );
};

// ============================================================================
// Radio Button Group Component (Tab-like buttons)
// ============================================================================

export const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
    size = 'middle',
    buttonStyle = 'outline',
    className,
    ...props
}) => {
    return (
        <>
            <style>{getRadioButtonStyles(buttonStyle)}</style>
            <AntRadio.Group
                optionType="button"
                buttonStyle={buttonStyle}
                size={size}
                className={cn('ds-radio-button-group', `ant-radio-group-${size}`, className)}
                {...props}
            />
        </>
    );
};

// ============================================================================
// Radio Card Component (Card-style selection)
// ============================================================================

export const RadioCard: React.FC<RadioCardProps> = ({
    title,
    description,
    icon,
    size = 'middle',
    className,
    checked,
    disabled,
    ...props
}) => {
    const config = sizeConfig[size];

    return (
        <>
            <style>{getRadioStyles(size)}</style>
            <label
                className={cn(
                    'flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200',
                    disabled && 'cursor-not-allowed opacity-60',
                    className
                )}
                style={{
                    borderColor: checked && !disabled ? colors.primary : colors.surfaceSubtle,
                    backgroundColor: checked && !disabled ? colors.primaryExtraLight : colors.surfaceLightest,
                }}
            >
                <AntRadio
                    checked={checked}
                    disabled={disabled}
                    className={`ds-radio-${size}`}
                    {...props}
                />
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        {icon && (
                            <span style={{ color: checked ? colors.primary : colors.textMuted }}>
                                {icon}
                            </span>
                        )}
                        <span
                            className={cn(config.fontSize, 'font-semibold')}
                            style={{ color: disabled ? colors.textMuted : colors.textDarkest }}
                        >
                            {title}
                        </span>
                    </div>
                    {description && (
                        <p
                            className="text-sm mt-1"
                            style={{ color: colors.textMuted }}
                        >
                            {description}
                        </p>
                    )}
                </div>
            </label>
        </>
    );
};

// ============================================================================
// Radio with Border Component
// ============================================================================

export type RadioPosition = 'left' | 'right';
export type RadioTextAlign = 'left' | 'center';

export interface RadioWithBorderProps extends RadioProps {
    /** Whether the radio is selected */
    selected?: boolean;
    /** Position of the radio button */
    radioPosition?: RadioPosition;
    /** Text alignment for label and description */
    textAlign?: RadioTextAlign;
}

export const RadioWithBorder: React.FC<RadioWithBorderProps> = ({
    size = 'middle',
    label,
    description,
    className,
    checked,
    disabled,
    children,
    radioPosition = 'left',
    textAlign = 'left',
    ...props
}) => {
    const config = sizeConfig[size];
    const isSelected = checked;

    const radioElement = (
        <AntRadio
            checked={checked}
            disabled={disabled}
            className={`ds-radio-${size}`}
            {...props}
        />
    );

    const textContent = (
        <span className={cn(
            "flex flex-col flex-1",
            textAlign === 'center' ? 'items-center text-center' : 'items-start'
        )}>
            <span
                className={cn(config.fontSize, 'font-semibold')}
                style={{ color: disabled ? colors.textMuted : colors.textDarkest }}
            >
                {label || children}
            </span>
            {description && (
                <span className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                    {description}
                </span>
            )}
        </span>
    );

    return (
        <>
            <style>{getRadioStyles(size)}</style>
            <label
                className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-200',
                    disabled && 'cursor-not-allowed opacity-60',
                    className
                )}
                style={{
                    borderColor: isSelected && !disabled ? colors.primary : colors.surfaceSubtle,
                    backgroundColor: isSelected && !disabled ? colors.primaryExtraLight : colors.white,
                }}
            >
                {radioPosition === 'left' ? (
                    <>
                        {radioElement}
                        {textContent}
                    </>
                ) : (
                    <>
                        {textContent}
                        {radioElement}
                    </>
                )}
            </label>
        </>
    );
};

// ============================================================================
// Default Export
// ============================================================================

export default Radio;
